import { ChatSession, ChatMessage, AIExpert } from '@garbera/shared';

export interface ChatHistoryItem extends ChatSession {
  expert: AIExpert;
  lastMessage: ChatMessage | null;
  messageCount: number;
  isPinned?: boolean;
}

export interface ChatPreviewData {
  id: number;
  title: string;
  expertName: string;
  lastMessage: string;
  lastMessageAt: string;
  messageCount: number;
  isActive: boolean;
}

export interface ChatHistoryFilters {
  expertId?: number;
  dateFrom?: string;
  dateTo?: string;
  searchText?: string;
  limit?: number;
  offset?: number;
}

export interface ChatHistoryResponse {
  sessions: ChatHistoryItem[];
  total: number;
  hasMore: boolean;
}

export interface ChatStatistics {
  totalSessions: number;
  totalMessages: number;
  favoriteExpert?: {
    id: number;
    name: string;
    sessionCount: number;
  };
  recentActivity: {
    thisWeek: number;
    thisMonth: number;
  };
}

export interface ChatHistoryState {
  sessions: ChatHistoryItem[];
  statistics: ChatStatistics | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  filters: ChatHistoryFilters;
}

export interface ChatHistoryContextType extends ChatHistoryState {
  loadChatHistory: (reset?: boolean) => Promise<void>;
  loadMoreSessions: () => Promise<void>;
  updateFilters: (filters: Partial<ChatHistoryFilters>) => void;
  deleteSession: (sessionId: number) => Promise<void>;
  archiveSession: (sessionId: number) => Promise<void>;
  updateSession: (sessionId: number, updates: { title?: string; isPinned?: boolean }) => Promise<void>;
  clearError: () => void;
  refreshStatistics: () => Promise<void>;
  refreshChatHistory: () => Promise<void>;
}

// Constants
export const CHAT_HISTORY_LIMITS = {
  INITIAL_LOAD: 20,
  LOAD_MORE: 10,
  MAX_TITLE_LENGTH: 50,
} as const;

export const DATE_RANGES = {
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
  ALL: 'all',
} as const;

export type DateRange = typeof DATE_RANGES[keyof typeof DATE_RANGES];